import { useLanguage } from '../context/LanguageContext';
import { Store, Users, Globe, Target, Zap, Shield, Award, TrendingUp, Building2, ShoppingBag } from 'lucide-react';

export default function AboutUs() {
  const { language } = useLanguage();

  const platforms = [
    {
      name: 'CloudGM Business',
      icon: Store,
      color: 'from-[#1E9BD9] to-[#0369A1]',
      description: language === 'ar'
        ? 'منصة التجار الشاملة لإنشاء وإدارة المتاجر الإلكترونية. تتيح للتجار إنشاء متاجر متخصصة في العقارات، السيارات، أرقام الهواتف المميزة، لوحات السيارات، قطع الغيار، والتجارة الإلكترونية العامة.'
        : 'The comprehensive merchant platform for creating and managing online stores. It allows merchants to create specialized stores in real estate, cars, premium phone numbers, car plates, spare parts, and general e-commerce.',
      features: language === 'ar' 
        ? ['إنشاء متاجر متعددة', 'لوحة تحكم متقدمة', 'إدارة المنتجات', 'تقارير المبيعات', 'دعم متعدد اللغات', 'نظام الوكلاء']
        : ['Create multiple stores', 'Advanced dashboard', 'Product management', 'Sales reports', 'Multi-language support', 'Agent system'],
      website: 'cloudgm.business'
    },
    {
      name: 'CloudGM',
      icon: ShoppingBag,
      color: 'from-[#F5A962] to-[#E88B30]',
      description: language === 'ar'
        ? 'السوق الإلكتروني الرائد في الإمارات للمنتجات والخدمات المتميزة. يجمع بين المشترين والبائعين في منصة واحدة موثوقة، مع تركيز خاص على الأرقام المميزة، لوحات السيارات الفاخرة، العقارات، والسيارات.'
        : 'The leading e-marketplace in UAE for premium products and services. It brings together buyers and sellers on one trusted platform, with a special focus on premium numbers, luxury car plates, real estate, and vehicles.',
      features: language === 'ar'
        ? ['تجربة تسوق سلسة', 'منتجات موثقة', 'بحث متقدم', 'مقارنة الأسعار', 'تقييمات المستخدمين', 'دعم على مدار الساعة']
        : ['Seamless shopping experience', 'Verified products', 'Advanced search', 'Price comparison', 'User reviews', '24/7 support'],
      website: 'cloudgm.ae'
    }
  ];

  const stats = [
    { value: '+5000', label: language === 'ar' ? 'تاجر نشط' : 'Active Merchants' },
    { value: '+50K', label: language === 'ar' ? 'معاملة شهرية' : 'Monthly Transactions' },
    { value: '95%', label: language === 'ar' ? 'رضا العملاء' : 'Customer Satisfaction' },
    { value: '24/7', label: language === 'ar' ? 'دعم متواصل' : 'Continuous Support' }
  ];

  const values = [
    {
      icon: Shield,
      title: language === 'ar' ? 'الأمان والثقة' : 'Security & Trust',
      description: language === 'ar' 
        ? 'نضمن أعلى معايير الأمان لحماية بياناتك ومعاملاتك'
        : 'We ensure the highest security standards to protect your data and transactions'
    },
    {
      icon: Zap,
      title: language === 'ar' ? 'السرعة والكفاءة' : 'Speed & Efficiency',
      description: language === 'ar'
        ? 'منصة سريعة وفعالة تمكنك من إدارة أعمالك بسهولة'
        : 'A fast and efficient platform that enables you to manage your business easily'
    },
    {
      icon: Target,
      title: language === 'ar' ? 'التركيز على العميل' : 'Customer Focus',
      description: language === 'ar'
        ? 'نضع احتياجات عملائنا في صميم كل ما نقوم به'
        : 'We put our customers\' needs at the heart of everything we do'
    },
    {
      icon: TrendingUp,
      title: language === 'ar' ? 'النمو المستمر' : 'Continuous Growth',
      description: language === 'ar'
        ? 'نساعدك على تنمية أعمالك وتحقيق أهدافك التجارية'
        : 'We help you grow your business and achieve your commercial goals'
    }
  ];

  return (
    <div className="cgm-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#1E9BD9] to-[#F5A962] py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-20 h-20 mx-auto bg-white/20 rounded-2xl flex items-center justify-center mb-6">
            <Globe className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white font-heading mb-4" data-testid="about-title">
            {language === 'ar' ? 'من نحن' : 'About Us'}
          </h1>
          <p className="text-lg sm:text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            {language === 'ar'
              ? 'CloudGM هي مجموعة من المنصات الرقمية المتكاملة التي تهدف إلى تمكين التجار والمستهلكين في الإمارات العربية المتحدة من خلال حلول تقنية مبتكرة وموثوقة'
              : 'CloudGM is a group of integrated digital platforms that aim to empower merchants and consumers in the UAE through innovative and reliable technological solutions'}
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10">
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <p className="text-2xl sm:text-3xl font-bold text-[#1E9BD9]">{stat.value}</p>
              <p className="text-gray-600 text-sm mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Our Story */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0F172A] font-heading mb-4">
            {language === 'ar' ? 'قصتنا' : 'Our Story'}
          </h2>
          <p className="text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {language === 'ar'
              ? 'انطلقت رحلتنا من رؤية واضحة: توفير منصة رقمية متكاملة تجمع بين التجار والمستهلكين في الإمارات. بدأنا بفكرة بسيطة وتطورنا لنصبح واحدة من أبرز المنصات الرقمية في المنطقة، نخدم آلاف التجار ونسهل ملايين المعاملات.'
              : 'Our journey started with a clear vision: to provide an integrated digital platform that brings together merchants and consumers in the UAE. We started with a simple idea and evolved to become one of the most prominent digital platforms in the region, serving thousands of merchants and facilitating millions of transactions.'}
          </p>
        </div>
      </section>

      {/* Platforms Section */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#0F172A] font-heading mb-4">
            {language === 'ar' ? 'منصاتنا' : 'Our Platforms'}
          </h2>
          <p className="text-gray-600">
            {language === 'ar' ? 'منظومة متكاملة من الحلول الرقمية' : 'An integrated ecosystem of digital solutions'}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {platforms.map((platform, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className={`bg-gradient-to-r ${platform.color} p-6 text-white`}>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                    <platform.icon className="h-7 w-7" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">{platform.name}</h3>
                    <p className="text-white/80 text-sm">{platform.website}</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <p className="text-gray-600 mb-6 leading-relaxed">{platform.description}</p>
                <div className="space-y-3">
                  <p className="font-semibold text-[#0F172A]">
                    {language === 'ar' ? 'المميزات الرئيسية:' : 'Key Features:'}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {platform.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#1E9BD9]"></div>
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Values Section */}
      <section className="bg-white py-16 mt-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0F172A] font-heading mb-4">
              {language === 'ar' ? 'قيمنا' : 'Our Values'}
            </h2>
            <p className="text-gray-600">
              {language === 'ar' ? 'المبادئ التي توجه عملنا يومياً' : 'The principles that guide our daily work'}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div key={index} className="text-center p-6 rounded-xl bg-[#F8FAFC] hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 mx-auto bg-[#1E9BD9]/10 rounded-xl flex items-center justify-center mb-4">
                  <value.icon className="h-7 w-7 text-[#1E9BD9]" />
                </div>
                <h3 className="font-bold text-[#0F172A] mb-2">{value.title}</h3>
                <p className="text-gray-600 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-gradient-to-br from-[#1E9BD9] to-[#0369A1] rounded-2xl p-8 text-white">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mb-6">
              <Target className="h-7 w-7" />
            </div>
            <h3 className="text-2xl font-bold mb-4">
              {language === 'ar' ? 'رؤيتنا' : 'Our Vision'}
            </h3>
            <p className="text-white/90 leading-relaxed">
              {language === 'ar'
                ? 'أن نكون المنصة الرقمية الرائدة في الإمارات والخليج العربي، ونقدم تجربة تجارية استثنائية تجمع بين الابتكار والموثوقية.'
                : 'To be the leading digital platform in the UAE and the Arabian Gulf, providing an exceptional commercial experience that combines innovation and reliability.'}
            </p>
          </div>

          <div className="bg-gradient-to-br from-[#F5A962] to-[#E88B30] rounded-2xl p-8 text-white">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mb-6">
              <Award className="h-7 w-7" />
            </div>
            <h3 className="text-2xl font-bold mb-4">
              {language === 'ar' ? 'مهمتنا' : 'Our Mission'}
            </h3>
            <p className="text-white/90 leading-relaxed">
              {language === 'ar'
                ? 'تمكين التجار ورواد الأعمال من خلال توفير أدوات ومنصات رقمية متطورة تساعدهم على النمو والنجاح في عالم التجارة الإلكترونية.'
                : 'Empowering merchants and entrepreneurs by providing advanced digital tools and platforms that help them grow and succeed in the e-commerce world.'}
            </p>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="bg-[#0F172A] py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white font-heading mb-4">
            {language === 'ar' ? 'هل لديك أسئلة؟' : 'Have Questions?'}
          </h2>
          <p className="text-gray-400 mb-8">
            {language === 'ar'
              ? 'فريقنا جاهز لمساعدتك والإجابة على جميع استفساراتك'
              : 'Our team is ready to help you and answer all your inquiries'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="mailto:support@cloudgm.business" 
              className="px-8 py-3 bg-[#1E9BD9] text-white rounded-xl font-semibold hover:bg-[#0369A1] transition-colors"
            >
              {language === 'ar' ? 'تواصل معنا' : 'Contact Us'}
            </a>
            <a 
              href="/" 
              className="px-8 py-3 bg-white/10 text-white rounded-xl font-semibold hover:bg-white/20 transition-colors"
            >
              {language === 'ar' ? 'الصفحة الرئيسية' : 'Home Page'}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
